const STORAGE_PREFIX = 'ordering-discard:'
export const DISCARD_MARKER_CHANNEL = 'ordering-discard'

// C2: 30-minute TTL for discard markers
const MARKER_TTL_MS = 30 * 60 * 1000

export type DiscardMarkerMessage = { type: 'set'; patientLookupId: string } | { type: 'clear'; patientLookupId: string }

/**
 * Opaque, non-PHI fingerprint of the patient lookup id (§8.3).
 *
 * The id reaches this function from two sources that spell the same patient
 * differently: the order form supplies a bare logical id, while the Epic launch
 * context supplies a `Patient/`-qualified one. A marker is written from the first
 * and cleared from the second, so the prefix is stripped here to keep both on one key.
 */
export function hashPatientLookupId(patientLookupId: string): string {
  const trimmed = patientLookupId.trim()
  const normalized = trimmed.startsWith('Patient/') ? trimmed.slice('Patient/'.length) : trimmed
  if (!normalized) return ''
  let hash = 0x811c9dc5
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

let persistentBroadcastChannel: BroadcastChannel | null = null
const localListeners = new Set<() => void>()

function getPersistentBroadcastChannel(): BroadcastChannel | null {
  if (persistentBroadcastChannel) return persistentBroadcastChannel
  if (typeof BroadcastChannel === 'undefined') return null
  try {
    persistentBroadcastChannel = new BroadcastChannel(DISCARD_MARKER_CHANNEL)
    return persistentBroadcastChannel
  } catch {
    return null
  }
}

function broadcast(message: DiscardMarkerMessage): void {
  getPersistentBroadcastChannel()?.postMessage(message)
  for (const listener of localListeners) {
    listener()
  }
}

/** True when auto-resume/create for this patient should be suppressed (R8). */
export function isDiscardMarkerSet(patientLookupId: string | null | undefined): boolean {
  if (!patientLookupId?.trim()) return false
  // NEW-1: No-op if normalized id is empty (e.g., "Patient/" → "")
  const hash = hashPatientLookupId(patientLookupId)
  if (!hash) return false
  try {
    const key = `${STORAGE_PREFIX}${hash}`
    const storedValue = localStorage.getItem(key)

    if (!storedValue) return false

    // C2: Parse as timestamp. Legacy '1' or unparseable values are migrated.
    const timestamp = parseInt(storedValue, 10)
    const now = Date.now()

    if (!Number.isFinite(timestamp)) {
      // S4: Unparseable — treat as corrupt and remove
      localStorage.removeItem(key)
      return false
    }

    // S4: Legacy '1' sentinel (epoch ~1970, age >> TTL) — migrate to now to preserve
    // suppression during upgrade window rather than immediately expiring.
    if (timestamp < 1000000000000) {
      // Before ~2001 (any pre-2001 timestamp is a legacy sentinel)
      localStorage.setItem(key, now.toString())
      return true // Treat as freshly set
    }

    // S3: Future-dated timestamp (clock skew) — treat as corrupt and remove rather
    // than granting a fresh TTL. Small skew tolerance (5 min) for genuine minor drift.
    const SKEW_TOLERANCE_MS = 5 * 60 * 1000
    if (timestamp > now + SKEW_TOLERANCE_MS) {
      localStorage.removeItem(key)
      return false
    }

    const age = now - timestamp

    if (age > MARKER_TTL_MS) {
      // Expired - clean up and return false
      localStorage.removeItem(key)
      return false
    }

    return true
  } catch {
    return false
  }
}

/** Mark patient so mount-time session create/resume is skipped until deliberately cleared. */
export function setDiscardMarker(patientLookupId: string): void {
  const id = patientLookupId.trim()
  if (!id) return
  // NEW-1: No-op if normalized id is empty (e.g., "Patient/" → "")
  const hash = hashPatientLookupId(id)
  if (!hash) return
  try {
    // C2: Store timestamp instead of '1'
    localStorage.setItem(`${STORAGE_PREFIX}${hash}`, Date.now().toString())
  } catch {
    // localStorage unavailable — best-effort only
  }
  broadcast({ type: 'set', patientLookupId: id })
}

/** Consumed by deliberate New Order so the same patient can be re-ordered. */
export function clearDiscardMarker(patientLookupId: string): void {
  const id = patientLookupId.trim()
  if (!id) return
  // NEW-1: No-op if normalized id is empty (e.g., "Patient/" → "")
  const hash = hashPatientLookupId(id)
  if (!hash) return
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${hash}`)
  } catch {
    // ignore
  }
  broadcast({ type: 'clear', patientLookupId: id })
}

/** Listen for marker changes in other tabs (storage + BroadcastChannel). */
export function subscribeDiscardMarkerChanges(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(STORAGE_PREFIX)) {
      onChange()
    }
  }
  window.addEventListener('storage', onStorage)
  localListeners.add(onChange)

  const channel = getPersistentBroadcastChannel()
  const onMessage = () => onChange()
  channel?.addEventListener('message', onMessage)

  return () => {
    window.removeEventListener('storage', onStorage)
    localListeners.delete(onChange)
    channel?.removeEventListener('message', onMessage)
  }
}
